import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { ConsultantService } from '../../services/consultant/consultant.service';
import type { IConsultantService } from '../../services/consultant/consultant.service.interface';
import { useValidatedTenantId } from '../auth/use-validated-tenant-id';
import { consultantQueryKeys } from './use-consultant-clients';

/**
 * Whether the signed-in user may open the consultant area. Backed by
 * `GET /consultant/access`, which mirrors the backend ConsultantGuard decision:
 * platform admin or Consulente membership on any tenant. Possedere link
 * referral o assegnazioni clienti manuali (partner/procacciatori) non basta.
 * `isPartner` gates the Partner nav/route: è vero se l'utente è Consulente o
 * Owner di sistema di un tenant con PartnerProfile attivo, anche mentre sta
 * lavorando nel workspace di un cliente assistito (l'area partner aggrega
 * tutti i clienti, non dipende dal workspace corrente).
 * `hasConsultantWorkspace` è il flag più stretto: dice se l'utente ha uno
 * studio (tenant CONSULTANT), su cui si fatturano richieste ai clienti, PDF e
 * IA. `consultantAiEnabled` aggiunge l'interruttore del back office: gating
 * delle FAB assistente nel builder e nello scadenzario (senza, ogni send
 * tornerebbe 403 `consultant_ai_disabled`). Le richieste guardano solo lo studio.
 *
 * La query key include il tenantId (per i platform admin `isPartner` è
 * tenant-scoped, quindi il flag deve rifare il fetch a ogni switch). Durante
 * lo switch `placeholderData: keepPreviousData` mantiene i flag del tenant
 * precedente finché il refetch non atterra, invece di far sparire per un
 * istante la sezione "Strumenti del consulente" dal drawer (i flag sono
 * cross-tenant per un utente non-admin, quindi il valore precedente resta
 * valido; per un platform admin è solo un placeholder transitorio).
 */
export function useConsultantAccess() {
  const consultantService = useInjection<IConsultantService>(ConsultantService);
  const tenantId = useValidatedTenantId();

  const {
    data: consultantAccess,
    isPending,
    fetchStatus,
  } = useQuery({
    queryKey: consultantQueryKeys.access(tenantId),
    queryFn: () => consultantService.hasConsultantAccess(),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  return {
    hasConsultantAccess: consultantAccess?.hasConsultantAccess ?? false,
    isPartner: consultantAccess?.isPartner ?? false,
    canViewCommissions: consultantAccess?.canViewCommissions ?? false,
    hasConsultantWorkspace: consultantAccess?.hasConsultantWorkspace ?? false,
    consultantAiEnabled: consultantAccess?.consultantAiEnabled ?? false,
    hasAcceptedManagedClientsClause: consultantAccess?.hasAcceptedManagedClientsClause ?? false,
    isPending,
    fetchStatus,
  };
}
